import "./AdminProductList.css";
import { adminGet } from '../adminFetch';
import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminProductList() {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("products");
  const [nameFilter, setNameFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const navigate = useNavigate();

  const renderProductPrice = (product) => {
    const isPriceValid = (price) => price !== null && price !== undefined && price > 0;
    const hasValidBatchStock = (product.batchStockQuantity !== null && product.batchStockQuantity > 0);
    const hasValidStock = (product.stockQuantity !== null && product.stockQuantity > 0);

    let hasVariantWithInvalidPrice = false;
    let validVariantPrices = [];

    if (product.variants && product.variants.length > 0) {
      validVariantPrices = product.variants
        .map(variant => variant.price)
        .filter(price => isPriceValid(price));
      
      hasVariantWithInvalidPrice = product.variants.some(variant => !isPriceValid(variant.price));
    }

    const isPreOrder = !hasValidStock ||
      hasVariantWithInvalidPrice ||
      ((!product.variants || product.variants.length === 0) && !isPriceValid(product.price));

    const elements = [];

    if (product.variants && product.variants.length > 0 && validVariantPrices.length > 0) {
      const minPrice = Math.min(...validVariantPrices);
      const maxPrice = Math.max(...validVariantPrices);
      
      if (minPrice === maxPrice) {
        elements.push(
          <span key="price" className="admin-product-list-price-value">
            RM {minPrice.toFixed(2)}
          </span>
        );
      } else {
        elements.push(
          <span key="price" className="admin-product-list-price-value">
            RM {minPrice.toFixed(2)} - RM {maxPrice.toFixed(2)}
          </span>
        );
      }
    } else if (isPriceValid(product.price)) {
      elements.push(
        <span key="price" className="admin-product-list-price-value">
          RM {product.price.toFixed(2)}
        </span>
      );
    }

    if (isPreOrder) {
      elements.push(
        <span key="preorder" className="admin-product-list-preorder-label">
          Pre-order
        </span>
      );
    }

    if (elements.length === 0) {
      return <span className="admin-product-list-no-price">Price not set</span>;
    } else if (elements.length === 1) {
      return elements[0];
    } else {
      return (
        <div className="admin-product-list-price-container">
          {elements[0]}{elements[1]}
        </div>
      );
    }
  };

  useEffect(() => {
    const fetchProducts = async () => {

      try {
        const response = await adminGet(`${API_BASE_URL}/products/selling`);
        if (!response.ok) {
          throw new Error("Failed to fetch products");
        }
        const data = await response.json();

        // Sort products alphabetically by name
        const sortedProducts = [...data.products].sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        setProducts(sortedProducts);
        setFilteredProducts(sortedProducts);
      } catch (error) {
        setError(error);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await adminGet(`${API_BASE_URL}/api/categories`);
        if (!response.ok) {
          throw new Error("Failed to fetch categories");
        }
        const data = await response.json();
        setCategories(data || []);
      } catch (error) {
        // Silent fail (previously only 1 console.error line in this catch block, no other actions)
      }
    };
    fetchProducts();
    fetchCategories();
  }, []);

  useEffect(() => {
    if (!products.length) return;

    // Filter products
    let filtered = [...products];

    if (nameFilter) {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(
        (product) => product.categoryID === parseInt(categoryFilter)
      );
    }

    // Sort products
    filtered.sort((a, b) => {
      const result = a.name.localeCompare(b.name);
      return sortOrder === "asc" ? result : -result;
    });

    setFilteredProducts(filtered);
  }, [products, nameFilter, categoryFilter, sortOrder]);

  const handleNameFilterChange = (e) => {
    setNameFilter(e.target.value);
  };

  const handleCategoryFilterChange = (e) => {
    setCategoryFilter(e.target.value);
  };

  const clearFilters = () => {
    setNameFilter("");
    setCategoryFilter("");
    setSortOrder("asc");
  };

  if (error) {
    return (
      <div className="admin-product-list-container">
        <div className="admin-product-list-error">Error: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="admin-product-list-container">
      {/* Back to Dashboard Button */}
      <div className="admin-product-list-header">
        <button
          className="admin-product-list-back-btn"
          onClick={() => navigate("/admin/dashboard")}
        >
          <i className="fas fa-arrow-left"></i> Back to Dashboard
        </button>
      </div>

      {/* Title Section */}
      <div className="admin-product-list-title">
        <h1>Product Management</h1>
      </div>

      {/* Tab Navigation Section */}
      <div className="admin-product-list-tabs">
        <button
          className={`admin-product-list-tab ${activeTab === "products" ? "active" : ""
            }`}
          onClick={() => setActiveTab("products")}
        >
          Products
        </button>
        <button
          className={`admin-product-list-tab ${activeTab === "categories" ? "active" : ""
            }`}
          onClick={() => navigate("/admin/category")}
        >
          Categories
        </button>
      </div>

      {/* Add New Product and View Remove Products Buttons */}
      <div className="admin-product-list-actions">
        <div className="admin-product-list-actions-left">
          <button
            className="admin-product-list-btn-manage"
            onClick={() => navigate("/admin/products/redeemable")}
          >
            <i className="fas fa-coins"></i> Manage Redeemable Products
          </button>
        </div>
        <div className="admin-product-list-actions-right">
          <button
            className="admin-product-list-btn-add"
            onClick={() => navigate("/admin/add-product")}
          >
            <i className="fas fa-plus"></i> Add New Product
          </button>
          <button
            className="admin-product-list-btn-delete"
            onClick={() => navigate("/admin/deleted-products/selling")}
          >
            <i className="fas fa-trash-alt"></i> View Removed Products
          </button>
        </div>
      </div>

      {/* Product List Section */}
      {activeTab === "products" && (
        <>
          {/* Filter Section */}
          <div className="admin-product-list-filter">
            <div className="admin-product-list-filter-group">
              <label>Search</label>
              <input
                type="text"
                placeholder="Filter by product name"
                value={nameFilter}
                onChange={handleNameFilterChange}
                className="admin-product-list-filter-input"
              />
            </div>
            <div className="admin-product-list-filter-group">
              <label>Category</label>
              <select
                value={categoryFilter}
                onChange={handleCategoryFilterChange}
              >
                <option value="">All Categories</option>
                {categories
                  .filter(category => !category.subCategories || category.subCategories.length === 0)
                  .map((category) => (
                    <option key={category.categoryID} value={category.categoryID}>
                      {category.name}
                    </option>
                  ))}
              </select>
            </div>
            {/* <div className="admin-product-list-filter-group">
              <label>Sort Order</label>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              >
                <option value="asc">A-Z</option>
                <option value="desc">Z-A</option>
              </select>
            </div> */}
            <button
              className="admin-product-list-filter-btn"
              onClick={clearFilters}
            >
              Search
            </button>
          </div>
          <div className="admin-product-list-grid">

            {filteredProducts.length === 0 ? (
              <div className="admin-product-list-no-results">
                <i className="fas fa-box"></i>
                <p className="primary-message"> No products found. Click "Add New Product" to create one.</p>
              </div>
            ) : (
              /* Product Cards Section */
              filteredProducts.map((product) => (
                <div
                  key={product.productID}
                  className="admin-product-list-card"
                >
                  <div className="admin-product-list-image-container">
                    <img
                      src={`${product.mainImageUrl}`}
                      alt={product.name}
                      className="admin-product-list-image"
                    />
                  </div>
                  <div className="admin-product-list-details">
                    <h2 className="admin-product-list-name">{product.name}</h2>
                    <span className={`admin-list-status ${product.isActive === false ? "inactive" : "active"}`}>
                      {product.isActive === false ? "Inactive" : "Active"}
                    </span>
                    <div className="admin-product-list-price">
                      {renderProductPrice(product)}
                    </div>
                    <div className="admin-product-list-info">
                      <div className="admin-product-list-detail">
                        <span className="admin-product-list-detail-label">
                          Product Code:
                        </span>
                        <span className="admin-product-list-detail-value">
                          {product.productCode}
                        </span>
                      </div>
                      <div className="admin-product-list-detail">
                        <span className="admin-product-list-detail-label">
                          Category:
                        </span>
                        <span className="admin-product-list-detail-value">
                          {categories.find(cat => cat.categoryID === product.categoryID)?.name || 'Unknown'}
                        </span>
                      </div>
                      <div className="admin-product-list-detail">
                        <span className="admin-product-list-detail-label">
                          UOM:
                        </span>
                        <span className="admin-product-list-detail-value">
                          {product.uom || "-"}
                        </span>
                      </div>
                      {/* <div className="admin-product-list-detail">
                        <span className="admin-product-list-detail-label">
                          Batch Stock:
                        </span>
                        <span className="admin-product-list-detail-value">
                          {product.batchStockQuantity ?? "0"}
                        </span>
                      </div> */}
                      <div className="admin-product-list-detail">
                        <span className="admin-product-list-detail-label">
                          Stock:
                        </span>
                        <span className="admin-product-list-detail-value">
                          {product.stockQuantity ?? "0"}
                          {product.stockQuantity < 50 && product.stockQuantity > 0 && (
                            <span className="admin-product-list-low-stock"> - Low Stock</span>
                          )}
                          {product.stockQuantity <= 0 && (
                            <span className="admin-product-list-low-stock"> - Out of Stock</span>
                          )}
                        </span>
                      </div>  
                    </div>
                  </div>
                  <div className="admin-product-list-actions-card">
                    <button
                      className="admin-product-list-btn-edit"
                      onClick={() =>
                        navigate(`/admin/edit-product/${product.productID}`)
                      }
                    >
                      <i className="far fa-edit"></i> Edit Product
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}

    </div>
  );
}

export default AdminProductList;
